import React from 'react';
import Layout from '@/components/Layout';

const Section = ({ number, title, children }) => (
  <section>
    <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text-primary)' }}>
      {number}. {title}
    </h2>
    <div className="space-y-3">{children}</div>
  </section>
);

const TermsPage = () => {
  return (
    <Layout>
      <div style={{ background: 'var(--color-background)' }} className="section-padding">
        <div className="container-custom max-w-4xl">
          <h1 className="text-5xl font-bold mb-4" style={{ color: 'var(--color-text-primary)' }}>
            Terms of Service
          </h1>
          <p className="mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            Last updated: 10 October 2026
          </p>
          <p className="mb-10 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            These Terms of Service ("Terms") are a legal agreement between you and Bamo Tech Sphere
            ("BTS", "we", "us", "our"), governing your access to and use of bamotechsphere.com and
            any related mobile or web application (together, the "Service"). They are written to comply
            with the laws of the Republic of Kenya, including the Consumer Protection Act, 2012, the Data
            Protection Act, 2019, the Kenya Information and Communications Act, 1998 (as amended), and the
            Computer Misuse and Cybercrimes Act, 2018, while remaining usable for customers accessing the
            Service from outside Kenya.
          </p>

          <div className="space-y-10" style={{ color: 'var(--color-text-secondary)' }}>
            <Section number="1" title="Who we are">
              <p>
                Bamo Tech Sphere is a technology retail business operating from Nairobi, Kenya, with branch
                locations as listed on our{' '}
                <a href="/store-locator" className="underline hover:text-accent">Store Locator</a> page.
              </p>
              <p className="text-sm italic">
                [Registered business/company name, Certificate of Incorporation or Business Registration
                number, and KRA PIN to be inserted here once formally registered, for full compliance with
                the Business Registration Service Act, 2015 and disclosure best practice.]
              </p>
            </Section>

            <Section number="2" title="Acceptance of these Terms">
              <p>
                By creating an account, browsing, placing an order, or otherwise using the Service, you
                confirm that you have read, understood, and agree to be bound by these Terms and our{' '}
                <a href="/privacy" className="underline hover:text-accent">Privacy Policy</a>. If you do not
                agree, you must not use the Service.
              </p>
            </Section>

            <Section number="3" title="Eligibility">
              <p>
                You must be at least 18 years old, or the age of majority in your jurisdiction, and have the
                legal capacity to enter into a binding contract to create an account or place an order. If
                you are under that age, you may only use the Service with the involvement and consent of a
                parent or legal guardian, who accepts these Terms on your behalf.
              </p>
            </Section>

            <Section number="4" title="Accounts">
              <p>You agree to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate, current, and complete information when registering</li>
                <li>Keep your login credentials confidential and notify us immediately of any unauthorised use</li>
                <li>Accept responsibility for all activity that occurs under your account</li>
              </ul>
              <p>
                We may suspend or terminate an account that provides false information, is used fraudulently,
                or breaches these Terms.
              </p>
            </Section>

            <Section number="5" title="Products, pricing and descriptions">
              <p>We make reasonable efforts to ensure product descriptions, images, and prices are accurate. However:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Prices are listed in Kenya Shillings (KES) unless stated otherwise, and are inclusive of Value Added Tax (VAT) where applicable under the VAT Act, 2013, except where shown as tax-exclusive for a specific currency or region</li>
                <li>Prices, specifications, and availability may change without prior notice</li>
                <li>Product images are for illustration and may differ slightly from the actual item (e.g. packaging updates by the manufacturer)</li>
                <li>If a listed price is materially incorrect due to a technical or typographical error, we will notify you before processing the order and give you the option to confirm at the correct price or cancel for a full refund</li>
                <li>We reserve the right to limit the quantity of any product sold to any person, household, or order</li>
              </ul>
            </Section>

            <Section number="6" title="Orders and payment">
              <p>By placing an order, you confirm that:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>All information you provide is accurate and complete</li>
                <li>You are authorised to use the payment method selected</li>
                <li>Payment must clear before an order is dispatched, except where we expressly offer pay-on-delivery</li>
              </ul>
              <p>
                We accept payment through licensed third-party payment providers, which may include M-Pesa
                (via a Central Bank of Kenya-licensed payment aggregator), Stripe, PayPal, and Skrill. We do
                not store your full card or mobile money PIN details — these are handled directly by our
                payment partners under their own security and regulatory obligations. We reserve the right to
                refuse or cancel an order where fraud, error, or a breach of these Terms is reasonably
                suspected, in which case any payment received will be refunded.
              </p>
            </Section>

            <Section number="7" title="Shipping and delivery">
              <p>
                Delivery timeframes shown at checkout are estimates, not guarantees. We are not liable for
                delays caused by courier partners, customs processes, weather, or other circumstances beyond
                our reasonable control. Risk in the goods passes to you on delivery to the address you provide;
                title passes once payment has been received in full.
              </p>
            </Section>

            <Section number="8" title="Cancellation, returns and refunds">
              <p>
                Consistent with the distance-selling protections in the Consumer Protection Act, 2012, you may
                cancel most orders and request a refund within 7 days of delivery, and return unused goods in
                their original packaging within 14 days of delivery for new products (7 days for clearly
                marked refurbished/open-box products), except where a product is excluded below.
              </p>
              <p>The following are not eligible for change-of-mind return once unsealed or activated, except where defective:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Items with a broken security seal (earbuds, hygiene-sensitive accessories)</li>
                <li>Digital licence keys, gift cards, and downloadable software</li>
                <li>Products made or configured to your specification</li>
              </ul>
              <p>
                This does not affect your statutory right to a repair, replacement, or refund for goods that
                are faulty, not as described, or unfit for purpose. Full procedure and timelines are set out in
                our <a href="/shipping" className="underline hover:text-accent">Shipping &amp; Returns</a> page.
              </p>
            </Section>

            <Section number="9" title="Warranty">
              <p>
                Products are covered by the applicable manufacturer warranty, the terms of which vary by
                product and brand. We assist in facilitating manufacturer warranty claims but are not
                ourselves the warrantor unless expressly stated on the product page. Nothing in this clause
                limits any warranty implied by law that cannot lawfully be excluded, including under the
                Consumer Protection Act, 2012.
              </p>
            </Section>

            <Section number="10" title="Intellectual property">
              <p>
                The Service, including its design, text, graphics, logos, and the "Bamo Tech Sphere" and
                "BTS" names and marks, is owned by or licensed to us and protected under the Copyright Act,
                2001 and the Trade Marks Act of Kenya. You may not copy, reproduce, or create derivative works
                from any part of the Service without our prior written consent, other than for your own
                personal, non-commercial use of the Service as intended.
              </p>
            </Section>

            <Section number="11" title="Acceptable use">
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access or interfere with the Service through unauthorised means, including hacking, scraping at scale, or introducing malware, in a manner contrary to the Computer Misuse and Cybercrimes Act, 2018</li>
                <li>Use the Service for any fraudulent, unlawful, or abusive purpose</li>
                <li>Submit reviews, images, or content that are defamatory, obscene, or infringe another person's rights</li>
                <li>Attempt to resell products purchased through the Service in a manner that breaches manufacturer distribution terms, without our written agreement</li>
              </ul>
            </Section>

            <Section number="12" title="Third-party links and services">
              <p>
                The Service may link to or integrate third-party websites and services (e.g. payment
                processors, WhatsApp, social media, Google/Facebook sign-in). We do not control and are not
                responsible for the content, policies, or practices of third parties. Your use of those
                services is governed by their own terms and privacy policies.
              </p>
            </Section>

            <Section number="13" title="Disclaimers and limitation of liability">
              <p>
                To the fullest extent permitted by Kenyan law, the Service is provided "as is" and "as
                available" without warranties of any kind beyond those that cannot lawfully be excluded. We
                are not liable for indirect, incidental, special, or consequential loss (including loss of
                profit or data) arising from your use of the Service. Where liability cannot be excluded, our
                total liability for any claim is limited to the amount you paid for the product or order
                giving rise to the claim. Nothing in these Terms excludes or limits liability for death,
                personal injury caused by negligence, or fraud, where such exclusion would be unlawful.
              </p>
            </Section>

            <Section number="14" title="Indemnity">
              <p>
                You agree to indemnify and hold Bamo Tech Sphere harmless against claims, losses, and
                expenses (including reasonable legal fees) arising from your breach of these Terms or misuse
                of the Service.
              </p>
            </Section>

            <Section number="15" title="Termination">
              <p>
                We may suspend or terminate your account or access to the Service at any time for breach of
                these Terms, suspected fraud, or legal/regulatory reasons, with notice where reasonably
                practicable. You may close your account at any time by contacting us; this does not affect
                orders already placed.
              </p>
            </Section>

            <Section number="16" title="Changes to these Terms">
              <p>
                We may update these Terms from time to time to reflect changes in our services or the law. The
                "Last updated" date above will change accordingly, and material changes will be highlighted on
                this page or communicated to registered users. Continued use of the Service after changes take
                effect constitutes acceptance of the revised Terms.
              </p>
            </Section>

            <Section number="17" title="Governing law and dispute resolution">
              <p>
                These Terms are governed by the laws of the Republic of Kenya. Any dispute arising from these
                Terms or your use of the Service will first be addressed through good-faith negotiation; if
                unresolved within 30 days, either party may refer the dispute to arbitration in Nairobi under
                the Arbitration Act, 1995, or to the courts of Kenya, which shall have non-exclusive
                jurisdiction. If you are a consumer accessing the Service from outside Kenya, nothing in this
                clause removes any mandatory consumer protection you are entitled to under the law of your own
                country of residence.
              </p>
            </Section>

            <Section number="18" title="Severability">
              <p>
                If any provision of these Terms is found unenforceable by a court or tribunal of competent
                jurisdiction, that provision will be limited or severed to the minimum extent necessary, and
                the remaining provisions will stay in full force.
              </p>
            </Section>

            <Section number="19" title="Contact us">
              <p>
                For questions about these Terms of Service:
                <br />
                Email: aethernexus05@gmail.com
                <br />
                Phone: +254 703 207 300
                <br />
                Registered address: Nairobi, Kenya
              </p>
            </Section>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TermsPage;
